export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// PayPal Client ID will be fetched from the backend
let PAYPAL_CLIENT_ID_CACHE = null;

export const getPayPalClientId = async () => {
  if (PAYPAL_CLIENT_ID_CACHE) {
    return PAYPAL_CLIENT_ID_CACHE;
  }
  
  try {
    const response = await fetch(`${API_URL}/paypal/config`);
    if (!response.ok) throw new Error('Failed to fetch PayPal config');
    const data = await response.json();
    PAYPAL_CLIENT_ID_CACHE = data.clientId;
    return data.clientId;
  } catch (error) {
    console.error('Error fetching PayPal config:', error);
    throw error;
  }
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Fonction pour gérer les erreurs d'authentification
const handleAuthError = (response) => {
  if (response.status === 401) {
    // Token expiré ou invalide
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Rediriger vers la page de connexion
    window.location.href = '/login';
    throw new Error('Session expirée. Veuillez vous reconnecter.');
  }
};

// ========== PRODUCTS ==========

export const getRandomProducts = async () => {
  try {
    const response = await fetch(`${API_URL}/products/random`);
    if (!response.ok) throw new Error('Failed to fetch random products');
    return await response.json();
  } catch (error) {
    console.error('Error fetching random products:', error);
    throw error;
  }
};

export const getProducts = async (page = 1, limit = 20, all = false) => {
  try {
    let url = `${API_URL}/products`;
    if (!all) {
      url += `?page=${page}&limit=${limit}`;
    }
    const response = await fetch(url);
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error:', response.status, errorText);
      throw new Error('Failed to fetch products');
    }
    const data = await response.json();
    return all ? data : data;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

export const getAllProducts = async () => {
  try {
    const response = await fetch(`${API_URL}/products/admin/all`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      handleAuthError(response);
      const errorText = await response.text();
      console.error('API Error:', response.status, errorText);
      throw new Error('Failed to fetch all products');
    }
    const data = await response.json();
    return data.products || [];
  } catch (error) {
    console.error('Error fetching all products:', error);
    throw error;
  }
};

export const getProduct = async (id) => {
  try {
    const response = await fetch(`${API_URL}/products/${id}`);
    if (!response.ok) throw new Error('Failed to fetch product');
    return await response.json();
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
};

export const createProduct = async (productData) => {
  try {
    const isFormData = productData instanceof FormData;
    const response = await fetch(`${API_URL}/products`, {
      method: 'POST',
      headers: isFormData ? {} : {
        'Content-Type': 'application/json',
      },
      body: isFormData ? productData : JSON.stringify(productData),
    });
    if (!response.ok) throw new Error('Failed to create product');
    return await response.json();
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
};

export const updateProduct = async (id, productData) => {
  try {
    const isFormData = productData instanceof FormData;
    const response = await fetch(`${API_URL}/products/${id}`, {
      method: 'PUT',
      headers: isFormData ? {} : {
        'Content-Type': 'application/json',
      },
      body: isFormData ? productData : JSON.stringify(productData),
    });
    if (!response.ok) throw new Error('Failed to update product');
    return await response.json();
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
};

export const deleteProduct = async (id) => {
  try {
    const response = await fetch(`${API_URL}/products/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete product');
    return await response.json();
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
};

export const rotateProductImage = async (id) => {
  try {
    const response = await fetch(`${API_URL}/products/${id}/rotate`, {
      method: 'POST',
    });
    if (!response.ok) throw new Error('Failed to rotate product image');
    return await response.json();
  } catch (error) {
    console.error('Error rotating product image:', error);
    throw error;
  }
};

export const toggleProductStatus = async (id, enabled) => {
  try {
    const response = await fetch(`${API_URL}/products/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ enabled }),
    });
    if (!response.ok) throw new Error('Failed to toggle product status');
    return await response.json();
  } catch (error) {
    console.error('Error toggling product status:', error);
    throw error;
  }
};

// ========== CATEGORIES ==========

export const getCategories = async () => {
  try {
    const response = await fetch(`${API_URL}/categories`);
    if (!response.ok) throw new Error('Failed to fetch categories');
    return await response.json();
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

export const createCategory = async (categoryData) => {
  try {
    const isFormData = categoryData instanceof FormData;
    const response = await fetch(`${API_URL}/categories`, {
      method: 'POST',
      headers: isFormData ? {} : {
        'Content-Type': 'application/json',
      },
      body: isFormData ? categoryData : JSON.stringify(categoryData),
    });
    if (!response.ok) throw new Error('Failed to create category');
    return await response.json();
  } catch (error) {
    console.error('Error creating category:', error);
    throw error;
  }
};

export const updateCategory = async (id, categoryData) => {
  try {
    const isFormData = categoryData instanceof FormData;
    const response = await fetch(`${API_URL}/categories/${id}`, {
      method: 'POST',
      headers: isFormData ? {} : {
        'Content-Type': 'application/json',
      },
      body: isFormData ? categoryData : JSON.stringify(categoryData),
    });
    if (!response.ok) throw new Error('Failed to update category');
    return await response.json();
  } catch (error) {
    console.error('Error updating category:', error);
    throw error;
  }
};

export const deleteCategory = async (id) => {
  try {
    const response = await fetch(`${API_URL}/categories/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete category');
    return await response.json();
  } catch (error) {
    console.error('Error deleting category:', error);
    throw error;
  }
};

export const getProductsByCategory = async (categoryId, page = 1, limit = 20) => {
  try {
    const response = await fetch(`${API_URL}/categories/${categoryId}/products?page=${page}&limit=${limit}`);
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error:', response.status, errorText);
      throw new Error('Failed to fetch products by category');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching products by category:', error);
    throw error;
  }
};

// ========== USERS ==========

export const getUsers = async () => {
  try {
    const response = await fetch(`${API_URL}/users`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      handleAuthError(response);
      throw new Error('Failed to fetch users');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const createUser = async (userData) => {
  try {
    const response = await fetch(`${API_URL}/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });
    if (!response.ok) throw new Error('Failed to create user');
    return await response.json();
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

export const updateUser = async (id, userData) => {
  try {
    const response = await fetch(`${API_URL}/users/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });
    if (!response.ok) throw new Error('Failed to update user');
    return await response.json();
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

export const deleteUser = async (id) => {
  try {
    const response = await fetch(`${API_URL}/users/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete user');
    return await response.json();
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

// ========== ORDERS ==========

export const getOrders = async () => {
  try {
    const response = await fetch(`${API_URL}/orders`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      handleAuthError(response);
      throw new Error('Failed to fetch orders');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
};

export const getOrder = async (id) => {
  try {
    const response = await fetch(`${API_URL}/orders/${id}`);
    if (!response.ok) throw new Error('Failed to fetch order');
    return await response.json();
  } catch (error) {
    console.error('Error fetching order:', error);
    throw error;
  }
};

export const updateOrder = async (id, orderData) => {
  try {
    const response = await fetch(`${API_URL}/orders/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(orderData),
    });
    if (!response.ok) throw new Error('Failed to update order');
    return await response.json();
  } catch (error) {
    console.error('Error updating order:', error);
    throw error;
  }
};

export const deleteOrder = async (id) => {
  try {
    const response = await fetch(`${API_URL}/orders/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete order');
    return await response.json();
  } catch (error) {
    console.error('Error deleting order:', error);
    throw error;
  }
};

// ========== CONTACTS ==========

export const getContacts = async () => {
  try {
    const response = await fetch(`${API_URL}/contacts`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      handleAuthError(response);
      throw new Error('Failed to fetch contacts');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching contacts:', error);
    throw error;
  }
};

export const createContact = async (contactData) => {
  try {
    const response = await fetch(`${API_URL}/contacts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contactData),
    });
    if (!response.ok) throw new Error('Failed to create contact');
    return await response.json();
  } catch (error) {
    console.error('Error creating contact:', error);
    throw error;
  }
};

export const deleteContact = async (id) => {
  try {
    const response = await fetch(`${API_URL}/contacts/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete contact');
    return await response.json();
  } catch (error) {
    console.error('Error deleting contact:', error);
    throw error;
  }
};

export const updateContact = async (id, contactData) => {
  try {
    const response = await fetch(`${API_URL}/contacts/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contactData),
    });
    if (!response.ok) throw new Error('Failed to update contact');
    return await response.json();
  } catch (error) {
    console.error('Error updating contact:', error);
    throw error;
  }
};
